import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { errorMessage, jobApi, publicRequest } from '@/lib/api';
import type {
  GeneratedJobDescription,
  Job,
  JobPosting,
  JobStats,
  OAuthConfig,
  Paginated,
  Platform,
  PlatformConnection,
} from '@/types';

export interface JobFilters {
  page: number;
  limit: number;
  search: string;
  status: string;
  jobType: string;
  sortBy: 'createdAt' | 'updatedAt' | 'title' | 'publishedAt';
  sortDir: 'asc' | 'desc';
}

export interface JobState {
  items: Job[];
  pagination: Paginated<Job>['pagination'] | null;
  current: Job | null;
  postings: JobPosting[];
  stats: JobStats | null;
  connections: PlatformConnection[];
  oauthConfigs: OAuthConfig[];
  filters: JobFilters;
  loading: boolean;
  saving: boolean;
  generating: boolean;
  error: string | null;
}

const initialState: JobState = {
  items: [],
  pagination: null,
  current: null,
  postings: [],
  stats: null,
  connections: [],
  oauthConfigs: [],
  filters: { page: 1, limit: 12, search: '', status: '', jobType: '', sortBy: 'createdAt', sortDir: 'desc' },
  loading: false,
  saving: false,
  generating: false,
  error: null,
};

/** Strips empty filter values so the API never sees `status=`. */
function toParams(filters: JobFilters): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== '' && value != null)
  ) as Record<string, string | number>;
}

export const fetchCompanyJobs = createAsyncThunk<Paginated<Job>, Partial<JobFilters> | void, { rejectValue: string }>(
  'jobs/fetchCompany',
  async (overrides, { getState, rejectWithValue }) => {
    const state = (getState() as { jobs: JobState }).jobs;
    try {
      const { data } = await jobApi.get<Paginated<Job>>('/company/jobs', {
        params: toParams({ ...state.filters, ...(overrides ?? {}) }),
      });
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load jobs'));
    }
  }
);

export const fetchPublicJobs = createAsyncThunk<Paginated<Job>, Record<string, unknown>, { rejectValue: string }>(
  'jobs/fetchPublic',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.get<Paginated<Job>>('/jobs', { params, ...publicRequest });
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load open roles'));
    }
  }
);

export const fetchJob = createAsyncThunk<Job, string, { rejectValue: string }>(
  'jobs/fetchOne',
  async (jobId, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.get<Job>(`/jobs/${jobId}`);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load this job'));
    }
  }
);

export const fetchPublicJob = createAsyncThunk<Job, string, { rejectValue: string }>(
  'jobs/fetchPublicOne',
  async (jobId, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.get<Job>(`/jobs/${jobId}`, publicRequest);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'This role is no longer available'));
    }
  }
);

export const fetchJobStats = createAsyncThunk<JobStats, void, { rejectValue: string }>(
  'jobs/stats',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.get<JobStats>('/company/jobs/stats');
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load job statistics'));
    }
  }
);

export const createJob = createAsyncThunk<Job, Partial<Job>, { rejectValue: string }>(
  'jobs/create',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.post<Job>('/jobs', payload);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not create the job'));
    }
  }
);

export const updateJob = createAsyncThunk<Job, { id: string; patch: Partial<Job> }, { rejectValue: string }>(
  'jobs/update',
  async ({ id, patch }, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.put<Job>(`/jobs/${id}`, patch);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not update the job'));
    }
  }
);

export const deleteJob = createAsyncThunk<string, string, { rejectValue: string }>(
  'jobs/delete',
  async (jobId, { rejectWithValue }) => {
    try {
      await jobApi.delete(`/jobs/${jobId}`);
      return jobId;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not delete the job'));
    }
  }
);

export const generateDescription = createAsyncThunk<
  GeneratedJobDescription,
  Record<string, unknown>,
  { rejectValue: string }
>('jobs/generateDescription', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await jobApi.post<GeneratedJobDescription>('/jobs/generate-description', payload);
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not generate a description'));
  }
});

export const fetchPostings = createAsyncThunk<JobPosting[], string, { rejectValue: string }>(
  'jobs/fetchPostings',
  async (jobId, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.get<{ data: JobPosting[] }>(`/jobs/${jobId}/postings`);
      return data.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load posting history'));
    }
  }
);

export const postExternally = createAsyncThunk<
  Array<{ platform: Platform; status: string; error?: string; externalUrl?: string }>,
  { jobId: string; platforms: Platform[] },
  { rejectValue: string }
>('jobs/postExternal', async ({ jobId, platforms }, { rejectWithValue }) => {
  try {
    const { data } = await jobApi.post<{ results: Array<{ platform: Platform; status: string }> }>(
      `/jobs/${jobId}/post-external`,
      { platforms }
    );
    return data.results;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not post to the selected platforms'));
  }
});

export const fetchConnections = createAsyncThunk<PlatformConnection[], void, { rejectValue: string }>(
  'jobs/fetchConnections',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.get<{ data: PlatformConnection[] }>('/platform-credentials');
      return data.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load platform connections'));
    }
  }
);

export const saveConnection = createAsyncThunk<
  PlatformConnection,
  { platform: Platform; credentials: Record<string, string> },
  { rejectValue: string }
>('jobs/saveConnection', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await jobApi.post<PlatformConnection>('/platform-credentials', payload);
    return data;
  } catch (err) {
    return rejectWithValue(errorMessage(err, 'Could not save those credentials'));
  }
});

export const removeConnection = createAsyncThunk<Platform, Platform, { rejectValue: string }>(
  'jobs/removeConnection',
  async (platform, { rejectWithValue }) => {
    try {
      await jobApi.delete(`/platform-credentials/${platform}`);
      return platform;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not disconnect'));
    }
  }
);

export const fetchOAuthConfigs = createAsyncThunk<OAuthConfig[], void, { rejectValue: string }>(
  'jobs/fetchOAuthConfigs',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.get<{ data: OAuthConfig[] }>('/oauth/config');
      return data.data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not load OAuth configuration'));
    }
  }
);

export const saveOAuthConfig = createAsyncThunk<OAuthConfig, Record<string, unknown>, { rejectValue: string }>(
  'jobs/saveOAuthConfig',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await jobApi.post<OAuthConfig>('/oauth/config', payload);
      return data;
    } catch (err) {
      return rejectWithValue(errorMessage(err, 'Could not save the OAuth application'));
    }
  }
);

const jobSlice = createSlice({
  name: 'jobs',
  initialState,
  reducers: {
    setFilters(state, action: { payload: Partial<JobFilters> }) {
      // Any filter change other than the page itself returns to page 1.
      const resetPage = Object.keys(action.payload).some((key) => key !== 'page');
      state.filters = { ...state.filters, ...action.payload, ...(resetPage ? { page: 1 } : {}) };
    },
    clearCurrentJob(state) {
      state.current = null;
      state.postings = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyJobs.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCompanyJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchCompanyJobs.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchPublicJobs.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.data;
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchJob.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchJob.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchJob.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchPublicJob.fulfilled, (state, action) => {
        state.loading = false;
        state.current = action.payload;
      })
      .addCase(fetchJobStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(createJob.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(createJob.fulfilled, (state, action) => {
        state.saving = false;
        state.items.unshift(action.payload);
        state.current = action.payload;
      })
      .addCase(createJob.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload ?? null;
      })
      .addCase(updateJob.pending, (state) => {
        state.saving = true;
      })
      .addCase(updateJob.fulfilled, (state, action) => {
        state.saving = false;
        state.current = action.payload;
        const index = state.items.findIndex((job) => job.id === action.payload.id);
        if (index >= 0) state.items[index] = action.payload;
      })
      .addCase(updateJob.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload ?? null;
      })
      .addCase(deleteJob.fulfilled, (state, action) => {
        state.items = state.items.filter((job) => job.id !== action.payload);
        if (state.current?.id === action.payload) state.current = null;
      })
      .addCase(generateDescription.pending, (state) => {
        state.generating = true;
      })
      .addCase(generateDescription.fulfilled, (state) => {
        state.generating = false;
      })
      .addCase(generateDescription.rejected, (state, action) => {
        state.generating = false;
        state.error = action.payload ?? null;
      })
      .addCase(fetchPostings.fulfilled, (state, action) => {
        state.postings = action.payload;
      })
      .addCase(fetchConnections.fulfilled, (state, action) => {
        state.connections = action.payload;
      })
      .addCase(saveConnection.fulfilled, (state, action) => {
        const index = state.connections.findIndex((c) => c.platform === action.payload.platform);
        if (index >= 0) state.connections[index] = action.payload;
        else state.connections.push(action.payload);
      })
      .addCase(removeConnection.fulfilled, (state, action) => {
        state.connections = state.connections.map((c) =>
          c.platform === action.payload ? { platform: c.platform, connected: false, status: null } : c
        );
      })
      .addCase(fetchOAuthConfigs.fulfilled, (state, action) => {
        state.oauthConfigs = action.payload;
      })
      .addCase(saveOAuthConfig.fulfilled, (state, action) => {
        const index = state.oauthConfigs.findIndex((c) => c.provider === action.payload.provider);
        if (index >= 0) state.oauthConfigs[index] = action.payload;
        else state.oauthConfigs.push(action.payload);
      });
  },
});

export const { setFilters, clearCurrentJob } = jobSlice.actions;
export default jobSlice.reducer;
